/**
 * Konfiguration och hjälpfunktioner för RSS-flöden i Woke News
 */
import { formatRelativeTimeLang, cleanTitle } from './i18nHelper'

export const COUNTRIES = [
  { id: 'INT', flag: '🌍', key: 'international', nameEn: 'International', nameDe: 'International', nameSv: 'Internationellt' },
  { id: 'DE', flag: '🇩🇪', key: 'germany', nameEn: 'Germany (EN)', nameDe: 'Deutschland (EN)', nameSv: 'Tyskland (EN)' },
  { id: 'SE', flag: '🇸🇪', key: 'sweden', nameEn: 'Sweden', nameDe: 'Schweden', nameSv: 'Sverige' },
  { id: 'all', flag: '🌐', key: 'allCountries', nameEn: 'All news', nameDe: 'Alle Nachrichten', nameSv: 'Alla nyheter' }
]

export const NEWS_SOURCES = [
  // Tyska källor på engelska
  {
    id: 'spiegel',
    name: 'Der Spiegel',
    shortName: 'SPIEGEL',
    badgeColor: '#e64415',
    textColor: '#ffffff',
    country: 'DE',
    feedUrls: [
      'https://www.spiegel.de/international/index.rss'
    ],
    defaultActive: true
  },
  {
    id: 'dw',
    name: 'Deutsche Welle',
    shortName: 'DW',
    badgeColor: '#002f6c',
    textColor: '#ffffff',
    country: 'DE',
    feedUrls: [
      'https://rss.dw.com/xml/rss-en-all'
    ],
    defaultActive: true
  },

  // Svenska källor
  {
    id: 'svt',
    name: 'SVT Nyheter',
    shortName: 'SVT',
    badgeColor: '#002f6c',
    textColor: '#ffffff',
    country: 'SE',
    feedUrls: [
      'https://www.svt.se/rss.xml'
    ],
    defaultActive: true
  },
  {
    id: 'etc',
    name: 'Dagens ETC',
    shortName: 'ETC',
    badgeColor: '#e50020',
    textColor: '#ffffff',
    country: 'SE',
    feedUrls: [
      'https://www.etc.se/rss.xml'
    ],
    defaultActive: true
  },
  {
    id: 'aftonbladet',
    name: 'Aftonbladet',
    shortName: 'AFTONBLADET',
    badgeColor: '#ffe500',
    textColor: '#121212',
    country: 'SE',
    feedUrls: [
      'https://rss.aftonbladet.se/rss2/small/pages/sections/senastenytt/'
    ],
    defaultActive: true
  },
  {
    id: 'dn',
    name: 'Dagens Nyheter',
    shortName: 'DN',
    badgeColor: '#0a3875',
    textColor: '#ffffff',
    country: 'SE',
    feedUrls: [
      'https://www.dn.se/rss/'
    ],
    defaultActive: true
  },
  {
    id: 'expressen',
    name: 'Expressen',
    shortName: 'EXPRESSEN',
    badgeColor: '#e30613',
    textColor: '#ffffff',
    country: 'SE',
    feedUrls: [
      'https://feeds.expressen.se/nyheter/',
      'https://feeds.expressen.se/nyheter/varlden/',
      'https://feeds.expressen.se/debatt/'
    ],
    defaultActive: true
  },
  {
    id: 'flamman',
    name: 'Flamman',
    shortName: 'FLAMMAN',
    badgeColor: '#9d0208',
    textColor: '#ffffff',
    country: 'SE',
    feedUrls: [
      'https://flamman.se/feed/'
    ],
    defaultActive: true
  },

  // Internationella källor
  {
    id: 'guardian',
    name: 'The Guardian',
    shortName: 'GUARDIAN',
    badgeColor: '#052962',
    textColor: '#ffe500',
    country: 'INT',
    feedUrls: [
      'https://www.theguardian.com/world/rss',
      'https://www.theguardian.com/us-news/rss'
    ],
    defaultActive: true
  },
  {
    id: 'nyt',
    name: 'The New York Times',
    shortName: 'NY TIMES',
    badgeColor: '#121212',
    textColor: '#ffffff',
    country: 'INT',
    feedUrls: [
      'https://rss.nytimes.com/services/xml/rss/nyt/World.xml',
      'https://rss.nytimes.com/services/xml/rss/nyt/HomePage.xml'
    ],
    defaultActive: true
  },
  {
    id: 'vox',
    name: 'Vox',
    shortName: 'VOX',
    badgeColor: '#fff200',
    textColor: '#121212',
    country: 'INT',
    feedUrls: [
      'https://www.vox.com/rss/index.xml'
    ],
    defaultActive: true
  },
  {
    id: 'npr',
    name: 'NPR',
    shortName: 'NPR',
    badgeColor: '#d62027',
    textColor: '#ffffff',
    country: 'INT',
    feedUrls: [
      'https://feeds.npr.org/1001/rss.xml'
    ],
    defaultActive: true
  },
  {
    id: 'atlantic',
    name: 'The Atlantic',
    shortName: 'ATLANTIC',
    badgeColor: '#2b2d42',
    textColor: '#ffffff',
    country: 'INT',
    feedUrls: [
      'https://www.theatlantic.com/feed/all/',
      'https://feeds.feedburner.com/TheAtlantic'
    ],
    defaultActive: true
  },
  {
    id: 'democracynow',
    name: 'Democracy Now!',
    shortName: 'DEMOCRACY NOW',
    badgeColor: '#005f73',
    textColor: '#ffffff',
    country: 'INT',
    feedUrls: [
      'https://www.democracynow.org/democracynow.rss'
    ],
    defaultActive: true
  }
]

/**
 * Klient-parser för RSS/Atom XML (använder webbläsarens inbyggda DOMParser)
 */
export function parseRssXml(xmlString, sourceMeta, lang = 'en') {
  const parser = new DOMParser()
  const doc = parser.parseFromString(xmlString, 'text/xml')
  const items = []

  // Hantera vanliga RSS 2.0 <item>
  const rssItems = doc.querySelectorAll('item')
  if (rssItems && rssItems.length > 0) {
    rssItems.forEach((el, index) => {
      const titleEl = el.querySelector('title')
      const linkEl = el.querySelector('link')
      const pubDateEl = el.querySelector('pubDate') || el.querySelector('date')
      const guidEl = el.querySelector('guid')

      const rawTitle = titleEl ? titleEl.textContent : ''
      const title = cleanTitle(rawTitle)
      let link = linkEl ? linkEl.textContent.trim() : ''
      if (!link && guidEl && guidEl.textContent.startsWith('http')) {
        link = guidEl.textContent.trim()
      }

      // Hämta kategori om den finns
      const categoryEl = el.querySelector('category')
      let category = categoryEl ? categoryEl.textContent.trim() : ''
      if (category) {
        category = category.replace(/<!\[CDATA\[(.*?)\]\]>/gs, '$1').replace(/<[^>]+>/g, '').trim()
        if (category.length > 25) category = category.slice(0, 25)
      } else if (sourceMeta.id === 'expressen' && link) {
        const subMatch = link.match(/expressen\.se\/nyheter\/([^/]+)\/([^/]+)/)
        if (subMatch && subMatch[1] && !subMatch[1].includes('-')) {
          const cat = subMatch[1].toLowerCase()
          if (cat === 'varlden') category = 'Världen'
          else if (cat === 'sverige') category = 'Sverige'
          else category = cat.charAt(0).toUpperCase() + cat.slice(1)
        } else {
          const secMatch = link.match(/expressen\.se\/(debatt|ledare|kultur|ekonomi|sport)\//)
          if (secMatch && secMatch[1]) {
            category = secMatch[1].charAt(0).toUpperCase() + secMatch[1].slice(1)
          } else if (link.includes('expressen.se/nyheter/')) {
            category = 'Nyheter'
          }
        }
      } else if (sourceMeta.id === 'atlantic' && link) {
        const m = link.match(/theatlantic\.com\/([^/]+)\/\d{4}\//)
        if (m && m[1]) {
          category = m[1].split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')
        }
      }

      if (title && link) {
        const pubDate = pubDateEl ? new Date(pubDateEl.textContent).toISOString() : new Date().toISOString()
        items.push({
          id: `${sourceMeta.id}-${index}-${encodeURIComponent(link).slice(-20)}`,
          title,
          link,
          pubDate,
          timeAgo: formatRelativeTimeLang(pubDate, lang),
          sourceId: sourceMeta.id,
          sourceName: sourceMeta.name,
          sourceShort: sourceMeta.shortName,
          badgeColor: sourceMeta.badgeColor,
          textColor: sourceMeta.textColor,
          country: sourceMeta.country || 'INT',
          category: category || undefined
        })
      }
    })
    return items
  }

  // Hantera Atom <entry>
  const atomEntries = doc.querySelectorAll('entry')
  if (atomEntries && atomEntries.length > 0) {
    atomEntries.forEach((el, index) => {
      const titleEl = el.querySelector('title')
      const linkEl = el.querySelector('link[rel="alternate"]') || el.querySelector('link')
      const updatedEl = el.querySelector('published') || el.querySelector('updated')

      const rawTitle = titleEl ? titleEl.textContent : ''
      const title = cleanTitle(rawTitle)
      const link = linkEl ? (linkEl.getAttribute('href') || linkEl.textContent.trim()) : ''

      // Hämta Atom kategori
      const categoryEl = el.querySelector('category')
      let category = ''
      if (categoryEl) {
        category = (categoryEl.getAttribute('term') || categoryEl.getAttribute('label') || categoryEl.textContent || '').trim()
        category = category.replace(/<!\[CDATA\[(.*?)\]\]>/gs, '$1').replace(/<[^>]+>/g, '').trim()
        if (category.length > 25) category = category.slice(0, 25)
      }

      if (title && link) {
        const pubDate = updatedEl ? new Date(updatedEl.textContent).toISOString() : new Date().toISOString()
        items.push({
          id: `${sourceMeta.id}-${index}-${encodeURIComponent(link).slice(-20)}`,
          title,
          link,
          pubDate,
          timeAgo: formatRelativeTimeLang(pubDate, lang),
          sourceId: sourceMeta.id,
          sourceName: sourceMeta.name,
          sourceShort: sourceMeta.shortName,
          badgeColor: sourceMeta.badgeColor,
          textColor: sourceMeta.textColor,
          country: sourceMeta.country || 'INT',
          category: category || undefined
        })
      }
    })
  }

  return items
}

/**
 * Fallback-artiklar för direkt presentation och offline-läge
 */
export const FALLBACK_NEWS = [
  {
    id: 'spiegel-1',
    title: 'Europe Looks Ahead: How Germany and Allies Are Reshaping Green Industrial Policy',
    link: 'https://www.spiegel.de/international/europe-looks-ahead-green-industrial-policy',
    pubDate: new Date(Date.now() - 1000 * 60 * 10).toISOString(),
    timeAgo: '10 min ago',
    sourceId: 'spiegel',
    sourceName: 'Der Spiegel',
    sourceShort: 'SPIEGEL',
    badgeColor: '#e64415',
    textColor: '#ffffff',
    country: 'DE'
  },
  {
    id: 'dw-1',
    title: 'Berlin Summit Concludes with Historic Agreement on International Humanitarian Aid',
    link: 'https://www.dw.com/en/berlin-summit-concludes-with-historic-agreement',
    pubDate: new Date(Date.now() - 1000 * 60 * 22).toISOString(),
    timeAgo: '22 min ago',
    sourceId: 'dw',
    sourceName: 'Deutsche Welle',
    sourceShort: 'DW',
    badgeColor: '#002f6c',
    textColor: '#ffffff',
    country: 'DE'
  },

  // Svenska nyheter
  {
    id: 'svt-1',
    title: 'Ny klimatutredning presenterad: Föreslår kraftigt utbyggd kollektivtrafik',
    link: 'https://www.svt.se/nyheter/inrikes/ny-klimatutredning-presenterad-foreslar-kollektivtrafik',
    pubDate: new Date(Date.now() - 1000 * 60 * 8).toISOString(),
    timeAgo: '8 min ago',
    sourceId: 'svt',
    sourceName: 'SVT Nyheter',
    sourceShort: 'SVT',
    badgeColor: '#002f6c',
    textColor: '#ffffff',
    country: 'SE',
    category: 'Klimat'
  },
  {
    id: 'expressen-1',
    title: 'Nya budgetförhandlingarna: Här är punkterna parterna är överens om',
    link: 'https://www.expressen.se/nyheter/politik/nya-budgetforhandlingarna-har-ar-punkterna',
    pubDate: new Date(Date.now() - 1000 * 60 * 14).toISOString(),
    timeAgo: '14 min ago',
    sourceId: 'expressen',
    sourceName: 'Expressen',
    sourceShort: 'EXPRESSEN',
    badgeColor: '#e30613',
    textColor: '#ffffff',
    country: 'SE',
    category: 'Politik'
  },
  {
    id: 'etc-1',
    title: 'Avslöjande: Bankernas rekordvinster slår hårdast mot unga och låginkomsttagare',
    link: 'https://www.etc.se/ekonomi/avslojande-bankernas-rekordvinster',
    pubDate: new Date(Date.now() - 1000 * 60 * 18).toISOString(),
    timeAgo: '18 min ago',
    sourceId: 'etc',
    sourceName: 'Dagens ETC',
    sourceShort: 'ETC',
    badgeColor: '#e50020',
    textColor: '#ffffff',
    country: 'SE',
    category: 'Ekonomi'
  },
  {
    id: 'aftonbladet-1',
    title: 'Just nu: Regeringen kallas till krismöte om skolsatsningar',
    link: 'https://www.aftonbladet.se/nyheter/a/just-nu-regeringen-kallas-till-krismote',
    pubDate: new Date(Date.now() - 1000 * 60 * 32).toISOString(),
    timeAgo: '32 min ago',
    sourceId: 'aftonbladet',
    sourceName: 'Aftonbladet',
    sourceShort: 'AFTONBLADET',
    badgeColor: '#ffe500',
    textColor: '#121212',
    country: 'SE',
    category: 'Samhälle'
  },
  {
    id: 'dn-1',
    title: 'Historiskt beslut om naturvård väntas i riksdagen i eftermiddag',
    link: 'https://www.dn.se/sverige/historiskt-beslut-om-naturvard-vantas-i-riksdagen',
    pubDate: new Date(Date.now() - 1000 * 60 * 48).toISOString(),
    timeAgo: '48 min ago',
    sourceId: 'dn',
    sourceName: 'Dagens Nyheter',
    sourceShort: 'DN',
    badgeColor: '#0a3875',
    textColor: '#ffffff',
    country: 'SE',
    category: 'Sverige'
  },
  {
    id: 'flamman-1',
    title: 'Fackförbunden kräver bindande regler mot hyvling av arbetstid',
    link: 'https://flamman.se/fackforbunden-kraver-bindande-regler',
    pubDate: new Date(Date.now() - 1000 * 60 * 65).toISOString(),
    timeAgo: '1 hour ago',
    sourceId: 'flamman',
    sourceName: 'Flamman',
    sourceShort: 'FLAMMAN',
    badgeColor: '#9d0208',
    textColor: '#ffffff',
    country: 'SE',
    category: 'Arbetsmarknad'
  },

  // Internationella nyheter
  {
    id: 'nyt-1',
    title: 'Global Climate Summit Reaches Landmark Accord on Renewable Energy Funding',
    link: 'https://www.nytimes.com/technology/investigation-big-tech-ai-transparency',
    pubDate: new Date(Date.now() - 1000 * 60 * 14).toISOString(),
    timeAgo: '14 min ago',
    sourceId: 'nyt',
    sourceName: 'The New York Times',
    sourceShort: 'NY TIMES',
    badgeColor: '#121212',
    textColor: '#ffffff',
    country: 'INT'
  },
  {
    id: 'guardian-1',
    title: 'Investigation Reveals Big Tech Lobbying Efforts Against AI Transparency Regulations',
    link: 'https://www.theguardian.com/technology/investigation-big-tech-ai-transparency',
    pubDate: new Date(Date.now() - 1000 * 60 * 28).toISOString(),
    timeAgo: '28 min ago',
    sourceId: 'guardian',
    sourceName: 'The Guardian',
    sourceShort: 'GUARDIAN',
    badgeColor: '#052962',
    textColor: '#ffe500',
    country: 'INT'
  },
  {
    id: 'vox-1',
    title: 'Why the 4-Day Workweek Movement is Accelerating Across European Economies',
    link: 'https://www.vox.com/economy/why-the-4-day-workweek-movement-is-accelerating',
    pubDate: new Date(Date.now() - 1000 * 60 * 45).toISOString(),
    timeAgo: '45 min ago',
    sourceId: 'vox',
    sourceName: 'Vox',
    sourceShort: 'VOX',
    badgeColor: '#fff200',
    textColor: '#121212',
    country: 'INT'
  },
  {
    id: 'atlantic-1',
    title: 'The Real Threat to Democratic Institutions Isn’t What You Think',
    link: 'https://www.theatlantic.com/ideas/real-threat-to-democratic-institutions',
    pubDate: new Date(Date.now() - 1000 * 60 * 70).toISOString(),
    timeAgo: '1 hour ago',
    sourceId: 'atlantic',
    sourceName: 'The Atlantic',
    sourceShort: 'ATLANTIC',
    badgeColor: '#2b2d42',
    textColor: '#ffffff',
    country: 'INT'
  },
  {
    id: 'npr-1',
    title: 'Supreme Court Weighs Milestone Free Speech and Digital Privacy Rights Case',
    link: 'https://www.npr.org/supreme-court-weighs-milestone-free-speech',
    pubDate: new Date(Date.now() - 1000 * 60 * 95).toISOString(),
    timeAgo: '1 hour ago',
    sourceId: 'npr',
    sourceName: 'NPR',
    sourceShort: 'NPR',
    badgeColor: '#d62027',
    textColor: '#ffffff',
    country: 'INT'
  },
  {
    id: 'democracynow-1',
    title: 'Grassroots Community Organizers Win Historic Victory on Affordable Housing Mandate',
    link: 'https://www.democracynow.org/grassroots-community-organizers-win-affordable-housing',
    pubDate: new Date(Date.now() - 1000 * 60 * 140).toISOString(),
    timeAgo: '2 hours ago',
    sourceId: 'democracynow',
    sourceName: 'Democracy Now!',
    sourceShort: 'DEMOCRACY NOW',
    badgeColor: '#005f73',
    textColor: '#ffffff',
    country: 'INT'
  }
]

